import { useCookie, useDayjs, useRequestEvent, useState } from '#imports'

// A token this close to `exp` may expire before the gateway checks it.
const EXPIRY_MARGIN_MS = 10_000

// The access token's time left at render: the browser cannot read the cookie.
const TIME_LEFT_AT_RENDER = 'core:access-token-time-left'

/** Milliseconds until the JWT's `exp`, read without verifying the token. */
function jwtTimeLeft(token: string | null | undefined, now: number): number | undefined {
    const payload = token?.split('.')[1]

    if (!payload) return

    try {
        const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))

        return typeof exp === 'number' ? exp * 1000 - now : undefined
    }
    catch {
        return
    }
}

export type AccessTokenState = 'fresh' | 'expiring' | 'none'

declare module 'h3' {
    interface H3EventContext {
        /** The access token the page render saw, for the server span. */
        _accessTokenState?: AccessTokenState
    }
}

function accessTokenState(timeLeft: number | undefined): AccessTokenState {
    if (timeLeft === undefined) return 'none'

    return timeLeft > EXPIRY_MARGIN_MS ? 'fresh' : 'expiring'
}

/**
 * During SSR: records the access token's time left, for `isAccessTokenFresh` in the browser, and its
 * state on the request for the server span.
 */
export function recordAccessTokenAtRender(): AccessTokenState {
    const timeLeft = jwtTimeLeft(useCookie('access_token', { readonly: true }).value, useDayjs()().valueOf())
    const event = useRequestEvent()
    const state = accessTokenState(timeLeft)

    if (event) event.context._accessTokenState = state
    if (timeLeft !== undefined) useState<number | null>(TIME_LEFT_AT_RENDER).value = timeLeft

    return state
}

/** During SSR, once `recordAccessTokenAtRender` ran: the access token's state. */
export function accessTokenStateAtRender(): AccessTokenState | undefined {
    return useRequestEvent()?.context._accessTokenState
}

/** In the browser: whether a gateway call made now would use the access token without refreshing it. */
export function isAccessTokenFresh(): boolean {
    const atRender = useState<number | null>(TIME_LEFT_AT_RENDER, () => null).value

    // Counted from navigation start, which precedes the render, so it errs on the short side.
    return atRender !== null && atRender - performance.now() > EXPIRY_MARGIN_MS
}
